import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';

/**
 * Admin: give an older athlete their own login. Creates a users(role='athlete')
 * account and links it to the roster record via athletes.user_id.
 */
function AdminAthleteAccounts() {
  const { user } = useAuth();
  const toast = useToast();
  const auth = { headers: { Authorization: `Bearer ${user?.token}` } };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingFor, setCreatingFor] = useState(null); // athlete_id
  const [form, setForm] = useState({ email: '', password: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/athlete-accounts`, auth);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Failed to load athlete accounts');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const startCreate = (row) => {
    setCreatingFor(row.athlete_id);
    setForm({ email: row.athlete_email || '', password: '' });
  };

  const submitCreate = async (row) => {
    if (!form.email || !form.password) { toast.error('Email and password are required'); return; }
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/admin/users`, {
        name: row.athlete_name, email: form.email, password: form.password, role: 'athlete'
      }, auth);
      const userId = res.data?.user?.id;
      await axios.post(`${API_BASE_URL}/api/admin/athlete-account-link`,
        { athleteId: row.athlete_id, userId }, auth);
      toast.success(`Login created for ${row.athlete_name}`);
      setCreatingFor(null);
      setForm({ email: '', password: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create login');
    } finally {
      setSaving(false);
    }
  };

  const unlink = async (row) => {
    if (!window.confirm(`Unlink ${row.account_email} from ${row.athlete_name}? Their login will no longer see this athlete.`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/athlete-account-link/${row.athlete_id}`, auth);
      load();
    } catch (err) {
      toast.error('Failed to unlink');
    }
  };

  const input = { padding: 8, border: '1px solid #d1d5db', borderRadius: 6 };

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: 20 }}>
      <h1>🧑‍🎓 Athlete Accounts</h1>
      <p style={{ color: '#666' }}>Give older athletes their own login. They'll see their schedule, videos + coach feedback, and a daily readiness check-in.</p>

      {loading ? <p>Loading…</p> : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid #eee' }}>Athlete</th>
              <th style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid #eee' }}>Login account</th>
              <th style={{ padding: 8, borderBottom: '2px solid #eee' }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.athlete_id}>
                <td style={{ padding: 8, borderBottom: '1px solid #f0f0f0' }}><strong>{row.athlete_name}</strong></td>
                <td style={{ padding: 8, borderBottom: '1px solid #f0f0f0' }}>
                  {row.user_id ? (
                    <span>{row.account_email}</span>
                  ) : creatingFor === row.athlete_id ? (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <input style={input} type="email" placeholder="login email"
                        value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                      <input style={input} placeholder="temp password (8+)"
                        value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                      <button className="btn-primary" disabled={saving} onClick={() => submitCreate(row)}>
                        {saving ? 'Saving…' : 'Create'}
                      </button>
                      <button className="btn-secondary" onClick={() => setCreatingFor(null)}>Cancel</button>
                    </div>
                  ) : (
                    <span style={{ color: '#999' }}>No login</span>
                  )}
                </td>
                <td style={{ padding: 8, borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                  {row.user_id ? (
                    <button className="btn-cancel" onClick={() => unlink(row)}>Unlink</button>
                  ) : creatingFor !== row.athlete_id ? (
                    <button className="btn-primary" onClick={() => startCreate(row)}>Create login</button>
                  ) : null}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={3} style={{ padding: 12, color: '#888' }}>No athletes on the roster yet.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default AdminAthleteAccounts;
